import { Sparkles } from 'lucide-react';
import { useCallback, useState } from 'react';
import { useAssistantEnabled } from './api.js';

const OPEN_KEY = 'nemo.assistant.open';

export interface AssistantShell {
  /** Whether this person may use it at all: the server's answer. */
  enabled: boolean;
  open: boolean;
  toggle: () => void;
  close: () => void;
}

/** Whether the panel is out, remembered per browser like the pane's width. */
export function useAssistantShell(signedIn: boolean): AssistantShell {
  const enabled = useAssistantEnabled(signedIn);
  const [open, setOpen] = useState(() => readOpen());
  const change = useCallback((next: boolean) => {
    setOpen(next);
    writeOpen(next);
  }, []);
  return {
    enabled,
    open: enabled && open,
    toggle: useCallback(() => change(!readOpen()), [change]),
    close: useCallback(() => change(false), [change]),
  };
}

/** The header's way in: an icon and the word, tinted while the panel is out. */
export function AssistantButton(props: { shell: AssistantShell }): React.JSX.Element | null {
  if (!props.shell.enabled) return null;
  return (
    <button
      type="button"
      className={props.shell.open ? 'assistant-button active' : 'assistant-button'}
      aria-pressed={props.shell.open}
      onClick={props.shell.toggle}
    >
      <Sparkles aria-hidden="true" />
      Assistant
    </button>
  );
}

function readOpen(): boolean {
  try {
    return window.localStorage.getItem(OPEN_KEY) === '1';
  } catch {
    return false;
  }
}

function writeOpen(open: boolean): void {
  try {
    window.localStorage.setItem(OPEN_KEY, open ? '1' : '0');
  } catch {
    // A private window simply does not remember.
  }
}
